@if (dialogData) {
  <section
    #modalContainer
    id="modal-container"
    class="flex flex-col bg-white items-center theme-wrapper overflow-y-auto max-h-screen relative"
    [ngClass]="
      dialogData.compact
        ? 'h-fit rounded-xl w-[90vw] max-w-[400px]'
        : 'h-screen md:h-fit w-screen md:w-[400px] md:rounded-xl'
    "
    (scroll)="onScroll($event)">
    <!-- 모바일 뒤로가기 헤더 (페이지 모달일 때만 표시) -->
    @if (dialogData.showCloseButton && !dialogData.compact) {
      <header
        class="sticky top-0 z-20 flex items-center justify-between bg-white/95 px-4 py-3 backdrop-blur-md border-b border-gray-100 w-full md:hidden">
        <button
          type="button"
          (click)="onClose()"
          class="flex size-10 items-center justify-center rounded-full text-gray-700 transition hover:bg-gray-100 -ml-2">
          <span class="icon-[material-symbols--arrow-back] text-2xl"></span>
        </button>
        <h1 class="flex-1 text-center text-lg font-bold leading-tight tracking-tight text-gray-900">
          {{ dialogData.headerText || dialogData.title }}
        </h1>
        <div class="size-10"></div>
      </header>
    }
    <!-- PC용 X 닫기 버튼 -->
    @if (dialogData.showCloseButton) {
      <button
        (click)="onClose()"
        class="hidden md:flex absolute top-3 right-3 z-20 size-8 items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
        <lepi-icon name="mdi:close" class="text-gray-500" size="md" />
      </button>
    }
    <!-- 아이콘 + 타이틀 헤더 (모바일 페이지 모달일 때는 숨김, PC에서는 항상 표시) -->
    <header
      class="sticky top-0 z-10 flex-col items-center w-full gap-3 p-4 theme-wrapper"
      [ngClass]="{
        flex: !dialogData.showCloseButton || dialogData.compact,
        'hidden md:flex': dialogData.showCloseButton && !dialogData.compact,
        'md:mt-0': dialogData.showCloseButton && !dialogData.compact,
      }">
      @if (!dialogData.image) {
        <lepi-icon [name]="iconName()" [color]="iconColor()" [ngClass]="iconClass()" size="lg" />
      }
      <h1 class="text-xl font-semibold theme-text text-center">
        {{ dialogData.title }}
      </h1>
    </header>

    @if (dialogData.data) {
      <div class="flex flex-col w-full gap-3 px-4 py-3 mb-6 border-gray-200 border-y">
        @for (data of dialogData.data; track $index) {
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-500">{{ data.key }}</span>
            <span class="text-sm">{{ data.value }}</span>
          </div>
        }
      </div>
    }

    @if (dialogData.inputs) {
      <form [formGroup]="form!" class="flex flex-col w-full gap-3 px-4 mb-6">
        @for (input of dialogData.inputs; track $index) {
          <lepi-fieldset [label]="input.label" [required]="input.required ? 'true' : 'false'">
            @if (input.type !== 'textarea') {
              <input
                lepiInput
                [type]="input.type"
                [placeholder]="input.placeholder ?? ''"
                [value]="input.defaultValue"
                [formControlName]="input.key"
                class="w-full input focus-within:border-primary" />
            } @else {
              <textarea
                lepiInput
                [formControlName]="input.key"
                class="w-full textarea focus-within:border-primary resize-none"
                [placeholder]="input.placeholder ?? ''"
                [value]="input.defaultValue"></textarea>
            }
          </lepi-fieldset>
        }
      </form>
    }

    <!-- 컨텐츠 영역 (이미지 + 텍스트) -->
    <div class="flex-1 flex flex-col items-center justify-center w-full px-6" [ngClass]="isPremium() ? 'py-8' : 'py-6'">
      @if (dialogData.image) {
        <div class="mb-6 w-full flex justify-center">
          <img
            [src]="dialogData.image.src"
            [alt]="dialogData.image.alt ?? ''"
            [class]="dialogData.image.class ?? ''"
            [ngClass]="[
              dialogData.image.width || (dialogData.image.class ? '' : 'w-56 md:w-48'),
              dialogData.image.height || 'h-auto',
            ]"
            [ngStyle]="dialogData.image.style ?? {}"
            class="object-contain" />
        </div>
      }

      <div class="flex flex-col gap-2 w-full text-center">
        @if (dialogData.highlight) {
          <div class="text-lg font-bold leading-snug tracking-tight" [ngClass]="iconClass()">
            {{ dialogData.highlight }}
          </div>
        }

        @if (dialogData.content) {
          <section class="flex items-center justify-center w-full">
            @if (!dialogData.markdown && !dialogData.html) {
              <div
                class="whitespace-pre-line text-pretty"
                [ngClass]="
                  isPremium()
                    ? 'text-gray-900 font-medium text-lg leading-snug tracking-tight'
                    : 'text-gray-600 text-base'
                ">
                {{ dialogData.content }}
              </div>
            } @else if (dialogData.html) {
              <div
                class="text-pretty"
                [ngClass]="
                  isPremium()
                    ? 'text-gray-900 font-medium text-lg leading-snug tracking-tight'
                    : 'text-gray-600 text-base'
                "
                [innerHTML]="dialogData.content"></div>
            } @else {
              <markdown
                class="text-center"
                [ngClass]="
                  isPremium()
                    ? 'text-gray-900 font-medium text-lg leading-snug tracking-tight'
                    : 'text-gray-600 text-base'
                "
                [data]="dialogData.content" />
            }
          </section>
        }

        @if (dialogData.description) {
          <p class="text-sm text-gray-400 mt-1 leading-relaxed break-keep whitespace-pre-line">
            {{ dialogData.description }}
          </p>
        }
      </div>
    </div>

    @if (dialogData.checkbox) {
      <lepi-checkbox class="mt-6" [label]="dialogData.checkbox.text" [(checked)]="checked" />
    }

    <footer
      class="sticky bottom-0 z-10 flex w-full gap-3 mt-6 border-t theme-wrapper border-gray-200 p-4"
      [ngClass]="{
        'flex-row-reverse': dialogData.reverseButtons,
      }">
      @if (!dialogData.buttons?.cancel?.hidden) {
        <button
          (click)="onCancel()"
          class="btn flex-1 btn-ghost"
          [ngClass]="{
            'h-14 text-base rounded-xl md:h-12 md:text-sm md:rounded-btn':
              dialogData.showCloseButton && !dialogData.compact,
          }">
          {{ dialogData.buttons?.cancel?.text ?? '취소' }}
        </button>
      }

      @if (dialogData.buttons?.confirm?.hidden !== true) {
        @let btnContent = dialogData.buttons?.confirm?.text ?? '확인';
        @let disabled = (!!form && form.invalid) || checkInvalid();
        @let isPageModal = dialogData.showCloseButton && !dialogData.compact;

        @switch (dialogData.type) {
          @case ('info') {
            <button
              [disabled]="disabled"
              class="btn flex-1 btn-primary"
              [ngClass]="{
                'h-14 text-base rounded-xl md:h-12 md:text-sm md:rounded-btn': isPageModal,
                'shadow-lg shadow-primary/30 md:shadow-none': isPageModal && isPremium(),
              }"
              (click)="onConfirm()">
              {{ btnContent }}
            </button>
          }
          @case ('success') {
            <button
              [disabled]="disabled"
              class="btn flex-1 btn-success"
              [ngClass]="{
                'h-14 text-base rounded-xl md:h-12 md:text-sm md:rounded-btn': isPageModal,
              }"
              (click)="onConfirm()">
              {{ btnContent }}
            </button>
          }
          @case ('warning') {
            <button
              [disabled]="disabled"
              class="btn flex-1 btn-warning"
              [ngClass]="{
                'h-14 text-base rounded-xl md:h-12 md:text-sm md:rounded-btn': isPageModal,
              }"
              (click)="onConfirm()">
              {{ btnContent }}
            </button>
          }
          @case ('error') {
            <button
              [disabled]="disabled"
              class="btn flex-1 btn-error"
              [ngClass]="{
                'h-14 text-base rounded-xl md:h-12 md:text-sm md:rounded-btn': isPageModal,
              }"
              (click)="onConfirm()">
              {{ btnContent }}
            </button>
          }
        }
      }
    </footer>
  </section>
}
